import { grey } from "@mui/material/colors";
import { GridColDef } from "@mui/x-data-grid";
import { DateTime } from "luxon";
import { motion } from "motion/react";
import { createElement, memo, useCallback, useMemo, useState } from "react";
import { TbCar, TbHeartHandshake, TbPasswordUser, TbQuestionMark } from "react-icons/tb";

import CommonPopup from "@/components/CommonPopup";
import { TableHeader } from "@/components/TableHeader";
import { driverStatuses } from "@/constants/driverStatuses";
import { useDriverStatusActions, useDriverStatusHoverStatus } from "@/stores/use-driver-status-table";
import { DriverStatus } from "@/types/daily-status.types";
import { UserType } from "@/types/user.types";

import { useDriverStatusContext } from "../../DriverStatusContext";
import DeleteDriverStatus from "../../components/DeleteDriverStatus";
import DriverStatusColumnTooltip from "../../components/DriverStatusColumnTooltip";
import DriverStatusInfoPopup from "../../components/DriverStatusInfoPopup";
import { DriverStatusType } from "../../types";

export enum DriverStatusModals {
    INFO = 1,
    DELETE,
    TOOLTIP,
}

function generateGradient(
    statuses: DriverStatus[],
    month: DateTime<true>,
    day: number,
    isActive: boolean,
    userType: UserType
) {
    if (!statuses.length) return null;

    const now = month.set({ day });
    const startOfDay = now.startOf("day"); // 00:00
    const endOfDay = now.endOf("day"); // 23:59

    const intervals = statuses
        .map(({ startTime, endTime, component }) => {
            return {
                start: DateTime.max(startTime, startOfDay).toMillis(),
                end: DateTime.min(endTime || endOfDay, endOfDay).toMillis(),
                component,
            };
        })
        .sort((a, b) => a.start - b.start);

    const totalDuration = intervals.reduce((sum, { start, end }) => sum + (start - end), 0);

    let lastPercent = 0;
    const gradientStops: {
        bg: string[];
        hover: string[];
    } = {
        bg: [],
        hover: [],
    };
    intervals.forEach(({ start, end, component }) => {
        const startPercent = Math.abs((lastPercent / totalDuration) * 100);
        const endPercent = Math.abs(((lastPercent + (end - start)) / totalDuration) * 100);

        gradientStops.bg.push(`${component?.class} ${startPercent.toFixed(2)}% ${endPercent.toFixed(2)}%`);
        gradientStops.hover.push(`${component?.hover} ${startPercent.toFixed(2)}% ${endPercent.toFixed(2)}%`);

        lastPercent += end - start;
    });

    return {
        component:
            (isActive
                ? userType === UserType.WELCOME_TEAM_OFFICER
                    ? () => TbHeartHandshake
                    : userType === UserType.DRIVER
                      ? () => TbCar
                      : () => TbPasswordUser
                : intervals.length === 1 && intervals[0].component?.component) || null,
        bg: `linear-gradient(to right, ${gradientStops.bg.join(", ")})`,
        hover: `linear-gradient(to right, ${gradientStops.hover.join(", ")})`,
    };
}

const MemoizedCell = memo(({ row, field, day }: { day: number; row: DriverStatusType; field: string }) => {
    const { month, pagePermissions } = useDriverStatusContext();
    const { handleHoveredIndex } = useDriverStatusActions();
    const hoveredIndex = useDriverStatusHoverStatus(day);
    const [activeState, setActiveState] = useState<DriverStatus | null>(null);
    const [deletionId, setDeletionId] = useState<string | null>(null);

    const [activeModal, setActiveModal] = useState<DriverStatusModals | null>(null);

    const handleActiveModal = (modal?: DriverStatusModals) => {
        setActiveModal(modal || null);
    };

    const reports = useMemo(() => {
        const list = [...((row[field] as DriverStatus[]) || [])] as DriverStatus[];
        for (const item of list) {
            item.component = driverStatuses.find((status) => status.status === item.status) || {
                status: "NONE",
                component: () => TbQuestionMark,
                class: "bg-transparent",
                hover: grey[500],
                payment: false,
                bg: "#fff",
                text: grey[600],
            };
        }
        return list;
    }, [row, field]);

    const isActive = useMemo(() => reports.some((item) => item.isActive), [reports]);

    const style = useMemo(
        () =>
            generateGradient(reports, month, day, isActive, row.userType as UserType) || {
                status: "NONE",
                component: () => TbQuestionMark,
                class: "transparent",
                hover: grey[500],
                payment: false,
                bg: grey[50],
            } ||
            "",
        [reports, isActive]
    );

    const [isHovered, setIsHovered] = useState(false);

    const handleHoverStart = useCallback(() => {
        handleHoveredIndex(day);
    }, [handleHoveredIndex]);
    const handleHoverEnd = useCallback(() => {
        handleHoveredIndex(-1);
    }, []);
    const handleDoubleClick = () => {
        if (!pagePermissions.edit) return;
        handleActiveModal(DriverStatusModals.INFO);
    };

    return (
        <div
            onMouseOver={handleHoverStart}
            onMouseOut={handleHoverEnd}
            className={"flex items-center h-full " + (hoveredIndex ? " border-x border-x-blue-200" : "")}
        >
            {style && (
                <DriverStatusColumnTooltip
                    handleCreate={() => handleActiveModal(DriverStatusModals.INFO)}
                    day={day}
                    handleUpdate={(item: DriverStatus) => {
                        setActiveState(item);
                        handleActiveModal(DriverStatusModals.INFO);
                    }}
                    activeModal={activeModal}
                    handleActiveModal={handleActiveModal}
                    handleDelete={(id: string) => {
                        handleActiveModal(DriverStatusModals.DELETE);
                        setDeletionId(id);
                    }}
                    child={
                        <motion.button
                            onDoubleClick={handleDoubleClick}
                            onMouseEnter={() => setIsHovered(true)}
                            onMouseLeave={() => setIsHovered(false)}
                            whileHover={{ scale: 1.25, background: style.hover, borderRadius: "10px" }}
                            whileTap={{ scale: 0.9 }}
                            key={style.bg}
                            style={{ background: isActive ? style.hover : style.bg }}
                            className={
                                (isActive ? "animate-pulse  " : "") +
                                "  w-full h-3/4 grid place-content-center    cursor-pointer  "
                            }
                        >
                            <motion.div
                                initial={{
                                    x: isActive ? 0 : "-80px",
                                    y: isActive ? 0 : "10px",
                                }}
                                animate={{
                                    x: isActive ? 0 : isHovered ? 0 : "-80px",
                                    y: isActive ? 0 : isHovered ? 0 : "10px",
                                }}
                                className="shadow"
                            >
                                {style.component &&
                                    createElement(style.component(), { fontSize: 25, color: grey[100] })}
                            </motion.div>
                        </motion.button>
                    }
                    statuses={reports}
                ></DriverStatusColumnTooltip>
            )}
            <CommonPopup
                title={"Kullanıcı Durumu" + (activeState ? " Güncelleme" : "")}
                size="sm"
                open={activeModal === DriverStatusModals.INFO}
                onClose={() => {
                    handleActiveModal();
                    setActiveState(null);
                }}
            >
                <DriverStatusInfoPopup
                    day={day}
                    row={row}
                    closePopup={() => {
                        handleActiveModal();
                        setActiveState(null);
                    }}
                    activeState={activeState}
                />
            </CommonPopup>
            <DeleteDriverStatus
                handleClose={() => {
                    handleActiveModal();
                    setDeletionId(null);
                }}
                deletionId={activeModal === DriverStatusModals.DELETE ? deletionId : null}
            />
        </div>
    );
});

export const DriverStatusColumn = (day: number): GridColDef<DriverStatusType> => {
    return {
        headerName: `${day}`,
        renderHeader: (params) => <TableHeader title={params.colDef.headerName!} />,
        field: "day" + day,
        width: 60,
        filterable: false,
        disableColumnMenu: true,
        disableExport: true,
        renderCell: (params) => <MemoizedCell row={params.row} field={params.colDef.field} day={day} />,
    };
};
